import { NOTARY_KEYWORDS_TOP, NOTARY_KEYWORDS_TOP_TOTAL } from "@/content/notary-corpus/top.gen";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { NOTARY_FAQ_CATEGORIES } from "@/content/notary-corpus/faq-defs";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/notary/faq/")({
  loader: async (): Promise<{ NOTARY_FAQ_TH: { q: string; a: string }[]; NOTARY_KEYWORDS: string[]; NOTARY_FAQ_DEEP: { q: string; a: string }[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/notary-corpus/th"),
      import("@/content/notary-corpus/keywords"),
    ]);
    return { NOTARY_FAQ_TH: m0.NOTARY_FAQ_TH, NOTARY_KEYWORDS: m1.NOTARY_KEYWORDS, NOTARY_FAQ_DEEP: m0.NOTARY_FAQ_TH_DEEP };
  },
  component: NotaryFaqHub,
  head: ({ loaderData }) => {
    const { NOTARY_FAQ_TH = [], NOTARY_KEYWORDS = [], NOTARY_FAQ_DEEP = [] } = loaderData ?? {};

    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `Notary Public FAQ — 240+ คำถามยอดฮิต 2026 | ${config.nameTh}`;
    const description = `รวมคำถามที่พบบ่อยเกี่ยวกับ Notary Public / Notarial Services Attorney ในประเทศไทย 240+ ข้อ ครอบคลุมราคา ขั้นตอน ประเภทเอกสาร Apostille และการรับรองระหว่างประเทศ อัปเดต 2026`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/notary/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/notary/faq" }],
        keywords: NOTARY_KEYWORDS_TOP.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Notary Public FAQ", url: `${site}/notary/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(NOTARY_FAQ_DEEP.length ? NOTARY_FAQ_DEEP : NOTARY_FAQ_TH)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "h2", ".faq-answer"],
            },
            keywords: NOTARY_KEYWORDS_TOP.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/notary/faq")) },
      ],
    };
  },
});

function NotaryFaqHub() {
  const { NOTARY_FAQ_TH, NOTARY_KEYWORDS } = Route.useLoaderData() as { NOTARY_FAQ_TH: { q: string; a: string }[]; NOTARY_KEYWORDS: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <span>Notary Public FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Notary Public FAQ — คำถามยอดฮิต {NOTARY_FAQ_TH.length}+ ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          รวมคำถามที่พบบ่อยเกี่ยวกับ Notary Public, Notarial Services Attorney, Apostille, และการรับรองเอกสารระหว่างประเทศในประเทศไทย —
          ตอบโดยทีมทนายผู้รับมอบอำนาจจากสภาทนายความ อัปเดตกฎหมายและอัตราค่าบริการปี 2026
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาอังกฤษ:</strong>{" "}
          <a href="/en/notary/faq" className="text-primary underline">
            English version — 250 FAQs for expats →
          </a>
        </div>

        <section aria-label="Notary FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">เลือกดูตามหมวด</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NOTARY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/notary/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{c.titleTh}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {NOTARY_FAQ_TH.map((item, i) => (
            <details
              key={i}
              className="rounded-lg border bg-card p-4 open:shadow-sm"
              {...(i < 5 ? { open: true } : {})}
            >
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ยังหาคำตอบไม่เจอ?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            ปรึกษาฟรีกับทนายผู้รับมอบอำนาจ — ตอบใน 15 นาที (จ.-ส. 9:00-18:00)
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ติดต่อทีมงาน →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">หัวข้อ Notary Public ยอดฮิต ({NOTARY_KEYWORDS_TOP_TOTAL} คำค้น)</h2>
          <p className="text-sm text-muted-foreground mb-4">
            รวมทุกคำค้นเกี่ยวกับบริการ Notary Public / Notarial Services Attorney ในประเทศไทย —
            ครอบคลุมกรุงเทพฯ ภูเก็ต เชียงใหม่ พัทยา และทุกจังหวัดหลัก
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_KEYWORDS.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">
                {kw}
              </span>
            ))}
          </div>
        </section>
      
        <PageGallery category="notary" seed="notary-faq-index" locale="th" />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}